import { Tooltip, styled } from "@mui/material";
import type { Icon } from "@phosphor-icons/react";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { FC, ReactNode } from "react";
import { CORE_ICON_BY_NAME, CoreIcon } from "../common/StatusIcon";
import type { CoreIconType } from "../common/StatusIcon";
import { Badge } from "../ui/badges";
import { IconNoHover } from "../ui/icons";
interface FixedWidthCellProps {
  size?: string;
  center?: boolean;
}

const FixedWidthCell = styled("td", {
  shouldForwardProp: (prop) => prop !== "size" && prop !== "center",
})<FixedWidthCellProps>(({ size, center }) => {
  return {
    width: size ? size : "100px",
    minWidth: size ? size : "100px",
    maxWidth: size ? size : "100px",
    wordBreak: "break-word",
    boxSizing: "border-box",
    textAlign: center ? "center" : "left",
    padding: "5px",
  };
});

const EventMessageCell = styled("td")`
  min-width: 200px;
  word-break: break-word;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const EventLogRowStyled = styled("tr")``;

const EventLogNarrowRowStyled = styled("div")`
  margin-bottom: 5px;
`;

interface BaseIconProps {
  tooltipText: string;
  children?: ReactNode;
  icon?: Icon;
}

const BaseIcon: FC<BaseIconProps> = ({ tooltipText, children, icon: Icon = null }) => (
  <Tooltip id="tooltip" title={tooltipText} arrow placement="right">
    <IconNoHover data-cy="tooltip-container-base-icon">{Icon ? <Icon /> : children}</IconNoHover>
  </Tooltip>
);

// "beta" has no icon of its own, so it falls through to the inactive icon.
const _santize_severity = (severity: string): CoreIconType => {
  severity = severity.toLowerCase();
  if (severity === "fatal" || severity === "error") {
    return "error";
  } else if (severity === "warn" || severity === "warning" || severity === "warn2") {
    return "warning";
  } else if (severity === "info" || severity === "info2") {
    return "info";
  } else {
    return CORE_ICON_BY_NAME[severity] ?? "inactive";
  }
};

interface EventLogStatusIconProps {
  severity?: string | null;
  willRetry?: boolean | null;
}

const EventLogStatusIcon = ({ severity, willRetry }: EventLogStatusIconProps) => {
  // `CoreIcon` fixes the colour per icon type, so nothing extra is needed for info.
  const iconType = _santize_severity(severity ?? "");
  if (willRetry === null || willRetry === undefined) {
    return <CoreIcon type={iconType} />;
  }
  return <EventLogStatusIconRetry severity={iconType} willRetry={willRetry} />;
};

const EventLogStatusIconRetry = ({ severity, willRetry }: { severity: string; willRetry?: boolean | null }) => {
  let text = "Will Retry";
  let tooltip_text = "This process will automatically retry later.";
  if (!willRetry) {
    text = "Will Not Retry";
    tooltip_text = "This process will not retry later.";
  }
  return (
    <Tooltip id="tooltip" title={tooltip_text} placement="bottom">
      <Badge icon={<WarningIcon />} size="small" severity={severity} label={text} />
    </Tooltip>
  );
};

export {
  BaseIcon,
  EventLogNarrowRowStyled,
  EventLogRowStyled,
  EventLogStatusIcon,
  EventLogStatusIconRetry,
  EventMessageCell,
  FixedWidthCell,
};
